import { Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * The standard page header, ported from Web/src/components/ui/PageHeader/.
 *
 * Title is `display2` (20px/500) in navy, subtitle `h4` (16px) in secondary —
 * the design system's proportions, which are noticeably smaller than the 32px
 * `h1` this app was using. `actions` is the equivalent of Web's
 * `PageHeader.Info` slot.
 *
 * The project pages use `project/ProjectHeaderBar` instead: it carries the
 * project's area navigation and metadata, which this deliberately does not.
 */
export type PageHeaderProps = {
  title: string;
  subtitle?: ReactNode;
  /** Material Symbols name, rendered before the title. */
  icon?: string;
  actions?: ReactNode;
  'data-automation-id'?: string;
};

export const PageHeader = ({
  title,
  subtitle,
  icon,
  actions,
  'data-automation-id': automationId,
}: PageHeaderProps) => (
  <Stack
    direction="row"
    sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
    data-automation-id={automationId}
  >
    <Stack gap={1}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
        {icon ? <MaterialSymbol name={icon} size={24} color="var(--fg-heading)" /> : null}
        <Typography variant="display2" component="h1" color="primary">
          {title}
        </Typography>
      </Stack>
      {subtitle ? (
        <Typography variant="h4" component="p" sx={{ color: 'var(--fg-2)' }}>
          {subtitle}
        </Typography>
      ) : null}
    </Stack>
    {actions}
  </Stack>
);
